/**
 * Checks whether a given authorization has access to a species module.
 *
 * Access restrictions apply only to REJEC (breeder) level.
 * Staff roles (BF, VF, KL, ADMIN, etc.) always have full access.
 *
 * The flags (dostopOvce, dostopKoze, dostopPrasici, dostopKonji)
 * are derived from active membership in a breeding program (rejski program)
 * and are provided by the backend on the subjekt object.
 */
import * as LTE from "#/def/lookup-tables.enums.gen";
import env from "#/env";

const MODULE_PIGS = "PIGS";
const MODULE_HORSES = "HORSES";
const MODULE_SMALL_LIVESTOCK = "SMALL_LIVESTOCK";

/** Priority order when redirecting a REJEC to another accessible module. */
const MODULES = [
  { key: MODULE_SMALL_LIVESTOCK, url: () => env.APP_URL_SMALL_LIVESTOCK },
  { key: MODULE_PIGS, url: () => env.APP_URL_PIGS },
  { key: MODULE_HORSES, url: () => env.APP_URL_HORSES },
];

export { MODULE_PIGS, MODULE_HORSES, MODULE_SMALL_LIVESTOCK };

/**
 * @returns {string|null} current app module key
 */
export function getCurrentModule() {
  if (env.isPigs) {
    return MODULE_PIGS;
  }
  if (env.isHorses) {
    return MODULE_HORSES;
  }
  if (env.isSmallLivestock) {
    return MODULE_SMALL_LIVESTOCK;
  }
  return null;
}

/**
 * @param {object|null} authorization - full authorization object (from useAuthorization().full)
 * @param {string} module - one of MODULE_PIGS, MODULE_HORSES, MODULE_SMALL_LIVESTOCK
 * @returns {boolean}
 */
export function hasModuleAccess(authorization, module) {
  if (!authorization) {
    return true;
  }

  if (authorization.nivoDostopa !== LTE.SifNivoDostopa.REJEC) {
    return true;
  }

  const { subjekt } = authorization;
  if (!subjekt) {
    return false;
  }

  switch (module) {
    case MODULE_PIGS:
      return !!subjekt.dostopPrasici;
    case MODULE_HORSES:
      return !!subjekt.dostopKonji;
    case MODULE_SMALL_LIVESTOCK:
      return !!(subjekt.dostopOvce || subjekt.dostopKoze);
    default:
      return true;
  }
}

/**
 * @param {object|null} authorization
 * @returns {string[]} accessible module keys for the given authorization
 */
export function getAccessibleModules(authorization) {
  return MODULES.filter((m) => hasModuleAccess(authorization, m.key)).map(
    (m) => m.key
  );
}

/**
 * Returns the URL of the first accessible module other than the current one.
 * @param {object|null} authorization
 * @param {string} currentModule
 * @returns {string|null}
 */
export function getRedirectModuleUrl(authorization, currentModule) {
  const fallback = MODULES.find(
    (m) => m.key !== currentModule && hasModuleAccess(authorization, m.key)
  );
  return fallback?.url() ?? null;
}

/**
 * Cross-app redirect preserving the selected authorization.
 * @param {string} url
 * @param {string|null} authorizationId
 * @param {string} presetAuthorizationParam
 */
export function redirectToModule(
  url,
  authorizationId,
  presetAuthorizationParam
) {
  const dst = new URL(url);
  if (authorizationId) {
    dst.searchParams.set(presetAuthorizationParam, authorizationId);
  }
  window.location.replace(dst.href);
}
